'use client';

import { useSiteCooperationsEditor } from "@/app/features/sites/cooperations/contexts/SiteCooperationsEditor";
import SlideshowIntroV2 from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntroV2";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { CooperationPartnersListFrame } from "@/app/features/sites/cooperations/ui/shared/CooperationsFrames";
import { Partner, PublicationStatus, UploadFile } from "@/app/lib/generated/graphql-types";
import { Box, Button, Collapse, Divider, IconButton, Paper, Stack, Typography } from "@mui/material";
import PartnerEditorProvider, {
    usePartnerEditor
} from "@/app/features/sites/cooperations/contexts/PartnerEditorContext";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import { MediaBrowserV2Dialog } from "@/app/features/shared/media-browser-v2";
import LinkEditor from "@/app/features/sites/admin/link-editor/ui/LinkEditor";
import EmailEditor from "@/app/features/sites/admin/link-editor/ui/EmailEditor";
import PhoneEditor, { PhoneEditorValue } from "@/app/features/sites/admin/link-editor/ui/PhoneEditor";
import { LinkEditable } from "@/app/features/shared/__to_be_integrated/types/forms/LinkSchema";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { DragEvent, FocusEvent, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Image from "next/image";
import { mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import EditMediaButtonOverlay from "@/app/features/shared/EditMediaButtonOverlay";
import { Check, Close, DragIndicator, KeyboardArrowDown, KeyboardArrowUp, Sort } from "@mui/icons-material";
import { updatePartner } from "@/app/features/sites/cooperations/data-access/mutations";
import { toast } from "react-toastify";

export default function CooperationsEditable({ partners }:  { partners: Partner[] }) {

    const { isLoading, editable } = useSiteCooperationsEditor();
    const [isSorting, setIsSorting] = useState(false);
    const [isSavingSortOrder, setIsSavingSortOrder] = useState(false);
    const [orderedPartners, setOrderedPartners] = useState(partners);
    const [draggedDocumentId, setDraggedDocumentId] = useState<string | null>(null);
    const rowRefs = useRef(new Map<string, HTMLElement>());

    useEffect(() => {
        if (!isSorting) {
            setOrderedPartners(partners);
        }
    }, [isSorting, partners]);

    if (isLoading || !editable) return <LoadingSpinner />;
    if (!editable) return null;

    function captureRowRects() {
        const rects = new Map<string, DOMRect>();
        rowRefs.current.forEach((element, documentId) => {
            rects.set(documentId, element.getBoundingClientRect());
        });
        return rects;
    }

    function animateRowSwap(previousRects: Map<string, DOMRect>) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) return;

        rowRefs.current.forEach((element, documentId) => {
            const previousRect = previousRects.get(documentId);
            if (!previousRect) return;

            const nextRect = element.getBoundingClientRect();
            const deltaY = previousRect.top - nextRect.top;
            if (Math.abs(deltaY) < 1) return;

            element.animate(
                [
                    { transform: `translate3d(0, ${deltaY}px, 0)` },
                    { transform: 'translate3d(0, 0, 0)' },
                ],
                {
                    duration: 160,
                    easing: 'cubic-bezier(0.2, 0, 0, 1)',
                }
            );
        });
    }

    function updateOrderedPartnersAnimated(update: (currentPartners: Partner[]) => Partner[]) {
        const previousRects = captureRowRects();

        flushSync(() => {
            setOrderedPartners(update);
        });

        requestAnimationFrame(() => animateRowSwap(previousRects));
    }

    function movePartner(fromIndex: number, toIndex: number) {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;

        updateOrderedPartnersAnimated((current) => {
            const next = [...current];
            const [movedPartner] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, movedPartner);
            return next;
        });
    }

    function moveDraggedPartner(targetDocumentId: string) {
        if (!draggedDocumentId || draggedDocumentId === targetDocumentId) return;

        const fromIndex = orderedPartners.findIndex((partner) => partner.documentId === draggedDocumentId);
        const toIndex = orderedPartners.findIndex((partner) => partner.documentId === targetDocumentId);
        movePartner(fromIndex, toIndex);
    }

    function handleDragOver(event: DragEvent<HTMLElement>, targetDocumentId: string) {
        if (!isSorting) return;

        event.preventDefault();
        if (!draggedDocumentId || draggedDocumentId === targetDocumentId) return;

        const targetRect = event.currentTarget.getBoundingClientRect();
        const targetMiddleY = targetRect.top + targetRect.height / 2;
        const draggedIndex = orderedPartners.findIndex((partner) => partner.documentId === draggedDocumentId);
        const targetIndex = orderedPartners.findIndex((partner) => partner.documentId === targetDocumentId);
        const shouldMoveDown = draggedIndex < targetIndex && event.clientY > targetMiddleY;
        const shouldMoveUp = draggedIndex > targetIndex && event.clientY < targetMiddleY;

        if (shouldMoveDown || shouldMoveUp) {
            movePartner(draggedIndex, targetIndex);
        }
    }

    async function confirmSortOrder() {
        setIsSavingSortOrder(true);

        try {
            const results = await Promise.all(orderedPartners.map((partner, index) => (
                updatePartner({
                    documentId: partner.documentId,
                    data: { order: index + 1 },
                    status: PublicationStatus.Draft,
                })
            )));
            const failedResult = results.find((result) => result.error);

            if (failedResult?.error) {
                throw failedResult.error;
            }

            toast.success("Reihenfolge als Entwurf gespeichert.");
            setIsSorting(false);
        } catch (error) {
            console.error("Error while updating partner order", error);
            toast.error("Reihenfolge konnte nicht gespeichert werden.");
        } finally {
            setIsSavingSortOrder(false);
        }
    }

    return (
        <>
            <SlideshowIntroV2 title={editable.intro.title} images={[editable.intro.image]} />

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="flex-end"
                sx={{ px: '10%', pt: 4 }}
            >
                {!isSorting && (
                    <Button startIcon={<Sort />} variant="outlined" onClick={() => setIsSorting(true)}>
                        Sortieren
                    </Button>
                )}
                {isSorting && (
                    <>
                        <Button
                            startIcon={<Close />}
                            variant="outlined"
                            disabled={isSavingSortOrder}
                            onClick={() => {
                                setOrderedPartners(partners);
                                setIsSorting(false);
                            }}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            startIcon={<Check />}
                            variant="contained"
                            disabled={isSavingSortOrder}
                            onClick={confirmSortOrder}
                        >
                            Reihenfolge übernehmen
                        </Button>
                    </>
                )}
            </Stack>

            <CooperationPartnersListFrame>
                {orderedPartners.map((partner, index) => (
                    <Box
                        key={partner.documentId || index}
                        ref={(element: HTMLElement | null) => {
                            if (element) {
                                rowRefs.current.set(partner.documentId, element);
                            } else {
                                rowRefs.current.delete(partner.documentId);
                            }
                        }}
                        draggable={isSorting}
                        onDragStart={() => setDraggedDocumentId(partner.documentId)}
                        onDragEnd={() => setDraggedDocumentId(null)}
                        onDragOver={(event) => handleDragOver(event, partner.documentId)}
                        onDrop={() => moveDraggedPartner(partner.documentId)}
                        sx={{
                            opacity: draggedDocumentId === partner.documentId ? 0.6 : 1,
                            willChange: isSorting ? 'transform' : 'auto',
                            transition: (theme) => theme.transitions.create(['opacity'], {
                                duration: theme.transitions.duration.shorter,
                                easing: theme.transitions.easing.easeInOut,
                            }),
                            '@media (prefers-reduced-motion: reduce)': {
                                transition: 'none',
                            },
                        }}
                    >
                        <PartnerEditorProvider documentId={partner.documentId}>
                            <PartnerRow
                                isSorting={isSorting}
                                orderNumber={index + 1}
                                onMoveUp={() => movePartner(index, index - 1)}
                                onMoveDown={() => movePartner(index, index + 1)}
                                canMoveUp={index > 0}
                                canMoveDown={index < orderedPartners.length - 1}
                            />
                        </PartnerEditorProvider>
                    </Box>
                ))}
            </CooperationPartnersListFrame>
        </>
    )
}

function PartnerRow({
    isSorting,
    orderNumber,
    onMoveUp,
    onMoveDown,
    canMoveUp,
    canMoveDown,
}: {
    isSorting: boolean;
    orderNumber: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
    canMoveUp: boolean;
    canMoveDown: boolean;
}) {

    const { editable, updateEditable, errorForField } = usePartnerEditor();
    const [isMediaBrowserOpen, setIsMediaBrowserOpen] = useState(false);
    const [activeEditor, setActiveEditor] = useState<'link' | 'email' | 'phone' | null>(null);

    if (!editable) return <LoadingSpinner />;

    function updateName(name: string) {
        updateEditable({ name });
    }

    function updateDescription(description: string) {
        updateEditable({ description });
    }

    function updateEmail(email?: string) {
        updateEditable({ email: email ?? null });
        if (email === undefined) {
            setActiveEditor((prev) => (prev === 'email' ? null : prev));
        }
    }

    function updatePhone(phone?: PhoneEditorValue) {
        if (!phone) {
            updateEditable({ phone: null });
            setActiveEditor((prev) => (prev === 'phone' ? null : prev));
            return;
        }

        updateEditable({
            phone: {
                id: phone.id ?? editable?.phone?.id ?? 'new',
                number: phone.number ?? '',
                reachabilityHint: phone.reachabilityHint ?? ''
            }
        });
    }

    function updateWebsite(website?: LinkEditable) {
        updateEditable({
            website: website ?? null
        });
        if (!website) {
            setActiveEditor((prev) => (prev === 'link' ? null : prev));
        }
    }

    function updateImage(image: MediaRef | null) {
        updateEditable({
            image
        });
        setIsMediaBrowserOpen(false);
    }

    const imageUrl = editable.image ? mediaRefUrl(editable.image) : null;
    const imageData = editable.image && typeof editable.image === 'object' && 'url' in editable.image
        ? editable.image
        : null;
    const imageWidth = imageData?.width ? Math.min(imageData.width, 500) : 300;
    const imageHeight = imageData?.height ? Math.min(imageData.height, 500) : 200;

    const isEditableTarget = (target: EventTarget | null) => {
        if (!(target instanceof HTMLElement)) return false;
        return target.matches('input, textarea, [contenteditable="true"]');
    };

    const handleEditorFocusCapture = (key: 'link' | 'email' | 'phone') => (event: FocusEvent<HTMLElement>) => {
        if (!isEditableTarget(event.target)) return;
        setActiveEditor(key);
    };

    const handleEditorBlurCapture = (key: 'link' | 'email' | 'phone') => (event: FocusEvent<HTMLElement>) => {
        const nextTarget = event.relatedTarget;
        if (event.currentTarget.contains(nextTarget as Node | null)) return;
        setActiveEditor((prev) => (prev === key ? null : prev));
    };

    const editorCellSx = (key: 'link' | 'email' | 'phone') => {
        const isInactive = activeEditor !== null && activeEditor !== key;

        return {
            minWidth: 0,
            width: { xs: '100%', md: isInactive ? 0 : 'auto' },
            flex: { xs: '1 1 auto', md: isInactive ? '0 0 0px' : '1 1 0' },
            maxWidth: { xs: '100%', md: isInactive ? 0 : '100%' },
            opacity: { xs: 1, md: isInactive ? 0 : 1 },
            overflow: 'hidden',
            pointerEvents: { xs: 'auto', md: isInactive ? 'none' : 'auto' },
            transition: (theme: { transitions: { create: (props: string[], options: { duration: number; easing: string }) => string; duration: { shorter: number }; easing: { easeInOut: string } } }) =>
                theme.transitions.create(['flex-basis', 'max-width', 'opacity', 'width'], {
                    duration: theme.transitions.duration.shorter,
                    easing: theme.transitions.easing.easeInOut,
                }),
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        };
    };

    return (
        <>
            <Divider />
            <Paper
                elevation={0}
                sx={(theme) => ({
                    p: isSorting ? 1.5 : 0,
                    border: isSorting ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
                    backgroundColor: isSorting ? theme.palette.action.hover : 'transparent',
                    borderRadius: 1,
                    transition: theme.transitions.create(['padding', 'border-color', 'background-color'], {
                        duration: theme.transitions.duration.shorter,
                        easing: theme.transitions.easing.easeInOut,
                    }),
                    '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                    },
                })}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    {isSorting && (
                        <>
                            <Typography
                                component="span"
                                color="primary"
                                sx={{ minWidth: '2ch', fontWeight: 700, textAlign: 'right' }}
                            >
                                {orderNumber}
                            </Typography>
                            <DragIndicator aria-hidden="true" sx={{ color: 'text.secondary', cursor: 'grab' }} />
                        </>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {isSorting ? (
                            <Typography variant="h2" color="primary" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                                {editable.name}
                            </Typography>
                        ) : (
                            <EditableTypographyV2
                                label="Name"
                                variant="h2"
                                color="primary"
                                value={editable.name}
                                onChange={updateName}
                                error={Boolean(errorForField?.('name'))}
                                helperText={errorForField?.('name') || undefined}
                            />
                        )}
                    </Box>

                    {isSorting && (
                        <Stack direction="row" spacing={0.5}>
                            <IconButton
                                size="small"
                                aria-label={`${editable.name} nach oben verschieben`}
                                disabled={!canMoveUp}
                                onClick={onMoveUp}
                            >
                                <KeyboardArrowUp />
                            </IconButton>
                            <IconButton
                                size="small"
                                aria-label={`${editable.name} nach unten verschieben`}
                                disabled={!canMoveDown}
                                onClick={onMoveDown}
                            >
                                <KeyboardArrowDown />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>

                <Collapse in={!isSorting} timeout="auto" unmountOnExit>
                    <Stack direction="row" spacing={2}>
                        {imageUrl && (
                            <Box
                                sx={{
                                    float: 'left',
                                    marginRight: '2rem',
                                    marginBottom: '1rem',
                                    width: '300px',
                                    maxWidth: '45%',
                                    height: 'auto',
                                    paddingTop: '0.5rem',
                                    position: 'relative',
                                    alignContent: 'center',
                                }}
                            >
                                <Image
                                    src={imageUrl}
                                    alt={imageData?.name ?? editable.name}
                                    width={imageWidth}
                                    height={imageHeight}
                                    style={{
                                        width: '100%',
                                        minWidth: '300px',
                                        height: 'auto',
                                        display: 'block',
                                    }}
                                />
                                <EditMediaButtonOverlay onImageChange={updateImage} />

                            </Box>
                        )}
                        {!imageUrl && (
                            <Button size="small" variant="contained" onClick={() => setIsMediaBrowserOpen(true)} sx={{ mb: 2 }}>
                                Bild auswählen
                            </Button>
                        )}
                        <EditableTypographyV2
                            label="Beschreibung"
                            value={editable.description}
                            onChange={updateDescription}
                            variant="body1"
                            error={Boolean(errorForField?.('description'))}
                            helperText={errorForField?.('description') || undefined}
                        />
                    </Stack>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                            alignItems: 'start',
                        }}
                    >
                        <Stack
                            spacing={1}
                            sx={editorCellSx('link')}
                            onFocusCapture={handleEditorFocusCapture('link')}
                            onBlurCapture={handleEditorBlurCapture('link')}
                        >
                            <Typography variant="overline" fontSize="small">Link</Typography>
                            <LinkEditor<LinkEditable>
                                value={editable.website || undefined}
                                onChange={updateWebsite}
                                rowPaddingTop={0}
                            />
                        </Stack>

                        <Stack
                            spacing={1}
                            sx={editorCellSx('email')}
                            onFocusCapture={handleEditorFocusCapture('email')}
                            onBlurCapture={handleEditorBlurCapture('email')}
                        >
                            <Typography variant="overline" fontSize="small">E-Mail</Typography>
                            <EmailEditor
                                value={editable.email}
                                onChange={updateEmail}
                                error={errorForField?.('email') || undefined}
                            />
                        </Stack>

                        <Stack
                            spacing={1}
                            sx={editorCellSx('phone')}
                            onFocusCapture={handleEditorFocusCapture('phone')}
                            onBlurCapture={handleEditorBlurCapture('phone')}
                        >
                            <Typography variant="overline" fontSize="small">Telefon</Typography>
                            <PhoneEditor
                                value={editable.phone}
                                onChange={updatePhone}
                                numberError={errorForField?.('phone.number') || undefined}
                                reachabilityHintError={errorForField?.('phone.reachabilityHint') || undefined}
                            />
                        </Stack>
                    </Box>
                </Collapse>
            </Paper>

            <MediaBrowserV2Dialog
                isOpen={isMediaBrowserOpen}
                onApply={(media) => updateImage(media as UploadFile | null)}
                onClose={() => setIsMediaBrowserOpen(false)}
            />
        </>
    )
}
